import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Fullscreen as FullscreenIcon,
  GetApp as DownloadIcon,
  Share as ShareIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

/**
 * CrewDashboard Component
 * Displays visualization dashboard for agentic crew project metrics and progress
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Project ID
 * @param {Object} props.project - Project data
 * @param {Array} props.crewTasks - Array of crew tasks
 * @param {Object} props.outputConfig - Output configuration settings
 * @param {Function} props.showNotification - Function to show notifications
 * @returns {React.ReactElement} CrewDashboard component
 */
const CrewDashboard = ({
  projectId,
  project,
  crewTasks = [],
  outputConfig = {},
  showNotification
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    pendingTasks: 0,
    activeAgents: 0,
    totalAgents: 0,
    averageCompletionTime: 0,
    successRate: 0,
    recentActivities: [],
    performanceMetrics: {}
  });
  
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // Calculate dashboard metrics
  const calculateMetrics = useCallback(() => {
    const totalTasks = crewTasks.length;
    const completedTasks = crewTasks.filter(task => task.status === 'completed').length;
    const inProgressTasks = crewTasks.filter(task => task.status === 'in_progress').length;
    const pendingTasks = crewTasks.filter(task => task.status === 'pending').length;
    
    // Group tasks by section to count agents
    const sections = {};
    crewTasks.forEach(task => {
      const sectionName = task.sectionName || 'Other';
      if (!sections[sectionName]) {
        sections[sectionName] = [];
      }
      sections[sectionName].push(task);
    });
    
    const totalAgents = Object.keys(sections).length * 3; // Assuming 3 agents per section on average
    const activeAgents = Object.keys(sections).filter(section => 
      sections[section].some(task => task.status === 'in_progress')
    ).length * 3;
    
    const successRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    
    // Mock recent activities
    const recentActivities = [
      { id: 1, type: 'task_completed', message: 'Crew Formation task completed by Former', timestamp: new Date(Date.now() - 30000), status: 'success' },
      { id: 2, type: 'agent_assigned', message: 'Dataly assigned to Data Analysis task', timestamp: new Date(Date.now() - 120000), status: 'info' },
      { id: 3, type: 'task_started', message: 'Content Strategy task started by Stratey', timestamp: new Date(Date.now() - 300000), status: 'info' },
      { id: 4, type: 'milestone_reached', message: 'Research Planning phase 70% complete', timestamp: new Date(Date.now() - 600000), status: 'success' },
      { id: 5, type: 'integration_setup', message: 'MCP tools configured for automation', timestamp: new Date(Date.now() - 900000), status: 'warning' }
    ];
    
    setDashboardData({
      totalTasks,
      completedTasks,
      inProgressTasks,
      pendingTasks,
      activeAgents,
      totalAgents,
      averageCompletionTime: 45, // Mock data in minutes
      successRate,
      recentActivities,
      performanceMetrics: {
        efficiency: 85,
        collaboration: 92,
        qualityScore: 88,
        timeToCompletion: 78
      }
    });
    
    setLastUpdated(new Date());
  }, [crewTasks]);

  // Auto-refresh dashboard data
  useEffect(() => {
    calculateMetrics();
    const interval = setInterval(calculateMetrics, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [calculateMetrics]);

  // Handle manual refresh
  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      calculateMetrics();
      showNotification({
        message: 'Dashboard data refreshed successfully',
        type: 'success'
      });
    } catch (error) {
      showNotification({
        message: 'Failed to refresh dashboard data',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [calculateMetrics, showNotification]);

  // Get status icon based on activity type
  const getActivityIcon = (type, status) => {
    const iconProps = { fontSize: 'small' };
    
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" {...iconProps} />;
      case 'warning':
        return <WarningIcon color="warning" {...iconProps} />;
      case 'error':
        return <ErrorIcon color="error" {...iconProps} />;
      default:
        return <InfoIcon color="info" {...iconProps} />;
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp.toLocaleDateString();
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Dashboard Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              color: '#1976d2',
              mb: 0.5,
              fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif"
            }}
          >
            📊 Crew Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh Dashboard">
            <IconButton 
              onClick={handleRefresh} 
              disabled={loading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dashboard Settings">
            <IconButton onClick={() => setShowSettingsDialog(true)}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Dashboard">
            <IconButton color="secondary">
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                  <AssignmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {dashboardData.totalTasks}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Tasks
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={dashboardData.successRate} 
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {dashboardData.completedTasks} completed • {dashboardData.inProgressTasks} in progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#388e3c', mr: 2 }}>
                  <GroupIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {dashboardData.activeAgents}/{dashboardData.totalAgents}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Active Agents
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(dashboardData.activeAgents / dashboardData.totalAgents) * 100} 
                color="success"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {Math.round((dashboardData.activeAgents / dashboardData.totalAgents) * 100)}% utilization
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#f57c00', mr: 2 }}>
                  <SpeedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {dashboardData.averageCompletionTime}m
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Avg. Completion
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="Within SLA" 
                color="success" 
                size="small" 
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#7b1fa2', mr: 2 }}>
                  <AssessmentIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {Math.round(dashboardData.successRate)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Success Rate
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={dashboardData.successRate} 
                color="secondary"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Quality: {dashboardData.performanceMetrics.qualityScore}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Metrics and Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Performance Metrics"
              action={
                <Tooltip title="View Details">
                  <IconButton>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                {Object.entries(dashboardData.performanceMetrics).map(([metric, value]) => (
                  <Grid item xs={6} sm={3} key={metric}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {value}%
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ textTransform: 'capitalize' }}>
                        {metric.replace(/([A-Z])/g, ' $1').trim()}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={value} 
                        sx={{ mt: 1, height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Recent Activity" />
            <CardContent sx={{ pt: 1 }}>
              <List dense>
                {dashboardData.recentActivities.slice(0, 5).map((activity) => (
                  <ListItem key={activity.id} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getActivityIcon(activity.type, activity.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {activity.message}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          {formatTimeAgo(activity.timestamp)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Button 
                fullWidth 
                variant="outlined" 
                size="small" 
                sx={{ mt: 1 }}
              >
                View All Activities
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dashboard Settings Dialog */}
      <Dialog 
        open={showSettingsDialog} 
        onClose={() => setShowSettingsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Dashboard Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Configure dashboard display preferences and refresh intervals.
          </Typography>
          {/* Add settings controls here */}
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            Settings panel coming soon...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setShowSettingsDialog(false)}>
            Save Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

CrewDashboard.propTypes = {
  projectId: PropTypes.string.isRequired,
  project: PropTypes.object,
  crewTasks: PropTypes.array,
  outputConfig: PropTypes.object,
  showNotification: PropTypes.func
};

export default CrewDashboard;